import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { ConfirmModal } from "./ConfirmModal";

/**
 * Operator-only editor for the heal cascade — the ordered list of steps the healer climbs
 * cheap→strong when a scraper breaks. Redesigned 2026-07-26 into ONE section: add a self-contained
 * step (name, description, API format, endpoint, model, key, price), then drag steps to reorder.
 * Each step's key is write-only — sent once, sealed server-side, shown back only as a last-4 hint.
 */

type HealFormat = "openai" | "anthropic" | "gemini";

interface Step {
  id: string;
  position: number;
  name: string;
  description: string | null;
  format: HealFormat;
  endpoint: string | null;
  model: string;
  price: { input: number; output: number };
  enabled: boolean;
  dataTermsAck: boolean;
  hasKey: boolean;
  hint: string | null;
  updatedAt: string;
  updatedBy: string | null;
}
interface LadderData {
  steps: Step[];
  encryptionConfigured: boolean;
}

const FORMATS: { value: HealFormat; label: string }[] = [
  { value: "openai", label: "OpenAI-compatible" },
  { value: "anthropic", label: "Anthropic" },
  { value: "gemini", label: "Gemini" },
];
const formatLabel = (f: HealFormat): string => FORMATS.find((x) => x.value === f)?.label ?? f;

/** Per-format endpoint guidance — the one field whose meaning changes with the format. */
function endpointHelp(f: HealFormat): { placeholder: string; hint: string; required: boolean } {
  if (f === "openai")
    return {
      placeholder: "https://api.groq.com/openai/v1/chat/completions",
      hint: "Full chat/completions URL. Required — this is any OpenAI-compatible provider (Groq, OpenAI, Together, DeepSeek, OpenRouter, a local server…).",
      required: true,
    };
  if (f === "anthropic")
    return {
      placeholder: "(optional) https://api.anthropic.com",
      hint: "Base URL. Optional — leave blank to use Anthropic's API directly.",
      required: false,
    };
  return {
    placeholder: "(optional) https://generativelanguage.googleapis.com/v1beta/models",
    hint: "Base URL. Optional — leave blank to use Google's API directly.",
    required: false,
  };
}

// ---- shared form state ----------------------------------------------------------------------

interface FormState {
  name: string;
  description: string;
  format: HealFormat;
  endpoint: string;
  model: string;
  apiKey: string;
  priceInput: string;
  priceOutput: string;
  enabled: boolean;
  dataTermsAck: boolean;
}

const emptyForm: FormState = {
  name: "",
  description: "",
  format: "openai",
  endpoint: "",
  model: "",
  apiKey: "",
  priceInput: "",
  priceOutput: "",
  enabled: true,
  dataTermsAck: false,
};

function formFromStep(s: Step): FormState {
  return {
    name: s.name,
    description: s.description ?? "",
    format: s.format,
    endpoint: s.endpoint ?? "",
    model: s.model,
    apiKey: "",
    priceInput: String(s.price.input),
    priceOutput: String(s.price.output),
    enabled: s.enabled,
    dataTermsAck: s.dataTermsAck,
  };
}

/**
 * A pasted API key carrying a non-Latin1 char (smart-quote / accented letter) or internal whitespace
 * blows up later when it's set as an HTTP `Authorization` header ("Cannot convert … to a ByteString").
 * Catch it at the source with a clear, field-level message instead of a cryptic error elsewhere.
 */
function apiKeyIssue(raw: string): string | null {
  const key = raw.trim();
  if (!key) return null;
  if (/\s/.test(key)) return "The API key has a space or line-break in it — remove the stray character.";
  for (const ch of key) {
    if (ch.charCodeAt(0) > 255) {
      return "The API key has a non-standard character (a smart-quote or accented letter from copy-paste?) — retype it.";
    }
  }
  return null;
}

/** True when the required fields are present (name, model, endpoint for openai) and the key is clean. */
function formValid(f: FormState): boolean {
  if (!f.name.trim() || !f.model.trim()) return false;
  if (f.format === "openai" && !f.endpoint.trim()) return false;
  if (apiKeyIssue(f.apiKey)) return false;
  return true;
}

function formToBody(f: FormState): Record<string, unknown> {
  return {
    name: f.name.trim(),
    description: f.description.trim() || undefined,
    format: f.format,
    endpoint: f.endpoint.trim() || undefined,
    model: f.model.trim(),
    price: { input: Number(f.priceInput) || 0, output: Number(f.priceOutput) || 0 },
    enabled: f.enabled,
    dataTermsAck: f.dataTermsAck,
    ...(f.apiKey.trim() ? { apiKey: f.apiKey.trim() } : {}),
  };
}

/** The editable field grid, shared by the add form and the inline editor. */
function StepFields({
  form,
  set,
  encryptionConfigured,
  keyPlaceholder,
}: {
  form: FormState;
  set: (patch: Partial<FormState>) => void;
  encryptionConfigured: boolean;
  keyPlaceholder: string;
}) {
  const help = endpointHelp(form.format);
  return (
    <div className="stack" style={{ gap: "var(--sp3)" }}>
      <label className="field">
        <span className="field-label">Name</span>
        <input
          value={form.name}
          maxLength={80}
          placeholder="e.g. Groq Llama 3.3 — cheap first pass"
          onChange={(e) => set({ name: e.target.value })}
        />
      </label>

      <label className="field">
        <span className="field-label">Description (optional)</span>
        <input
          value={form.description}
          maxLength={280}
          placeholder="What this step is for"
          onChange={(e) => set({ description: e.target.value })}
        />
      </label>

      <div className="row-gap" style={{ gap: "var(--sp3)", alignItems: "flex-end" }}>
        <label className="field" style={{ flex: "0 0 auto", width: 190 }}>
          <span className="field-label">API format</span>
          <select value={form.format} onChange={(e) => set({ format: e.target.value as HealFormat })}>
            {FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <span className="field-label">Model</span>
          <input
            value={form.model}
            maxLength={160}
            placeholder="e.g. llama-3.3-70b-versatile"
            onChange={(e) => set({ model: e.target.value })}
          />
        </label>
      </div>

      <label className="field">
        <span className="field-label">Endpoint{help.required ? "" : " (optional)"}</span>
        <input value={form.endpoint} placeholder={help.placeholder} onChange={(e) => set({ endpoint: e.target.value })} />
        <span className="muted sm">{help.hint}</span>
      </label>

      <label className="field">
        <span className="field-label">API key {keyPlaceholder === "paste API key" ? "" : "(paste to rotate)"}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={form.apiKey}
          disabled={!encryptionConfigured}
          placeholder={encryptionConfigured ? keyPlaceholder : "set LADDER_KEK to store keys"}
          onChange={(e) => set({ apiKey: e.target.value })}
        />
        {apiKeyIssue(form.apiKey) && <span className="err sm">{apiKeyIssue(form.apiKey)}</span>}
      </label>

      <div className="row-gap" style={{ gap: "var(--sp3)" }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="field-label">Price — $/M input</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.priceInput}
            placeholder="0"
            onChange={(e) => set({ priceInput: e.target.value })}
          />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="field-label">$/M output</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.priceOutput}
            placeholder="0"
            onChange={(e) => set({ priceOutput: e.target.value })}
          />
        </label>
      </div>

      <label className="row-gap sm" style={{ gap: "var(--sp2)", alignItems: "center" }}>
        <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled
      </label>
      <label className="row-gap sm" style={{ gap: "var(--sp2)", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={form.dataTermsAck}
          onChange={(e) => set({ dataTermsAck: e.target.checked })}
        />{" "}
        This endpoint may use submitted (customer) page content for training
      </label>
    </div>
  );
}

// ---- component ------------------------------------------------------------------------------

export function HealLadder() {
  const { data, loading, error, reload } = useAsync<LadderData>(() => api("/heal/ladder"), []);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Heal ladder</h1>
        <span className="muted">The cascade that repairs broken scrapers. Operator only.</span>
      </div>

      <p className="note">
        When a scraper breaks, healing tries each step in order — top (cheapest) first — and stops at
        the first fix that passes validation. A weak/cheap step can only save money or waste a round;
        it can never ship a bad fix (every candidate is validated against the page before it is used).
        Drag steps to reorder.
      </p>

      {!data.encryptionConfigured && (
        <div className="notice">
          Set the <code>LADDER_KEK</code> secret on the API/worker to store keys here. Until then you
          can lay out steps, but their key fields are disabled and a keyless step won't run — a key
          must never sit in the database unencrypted.
        </div>
      )}

      <StepList steps={data.steps} encryptionConfigured={data.encryptionConfigured} reload={reload} />
      <AddStep encryptionConfigured={data.encryptionConfigured} reload={reload} />
    </div>
  );
}

function reorder<T>(list: T[], from: number, to: number): T[] {
  const copy = [...list];
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

function StepList({
  steps,
  encryptionConfigured,
  reload,
}: {
  steps: Step[];
  encryptionConfigured: boolean;
  reload: () => void;
}) {
  const [items, setItems] = useState<Step[]>(steps);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);

  // Re-seed from the server copy whenever it changes (after add/edit/delete/reorder + reload).
  useEffect(() => setItems(steps), [steps]);

  async function persistOrder(next: Step[]) {
    setItems(next);
    try {
      await api("/heal/steps/order", { method: "PUT", body: JSON.stringify({ ids: next.map((s) => s.id) }) });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to save order");
      setItems(steps); // roll back to the server truth
    }
  }

  function onDrop(target: number) {
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from == null || from === target) return;
    void persistOrder(reorder(items, from, target));
  }

  if (items.length === 0) {
    return (
      <section className="card">
        <p className="muted" style={{ textAlign: "center", padding: "var(--sp5)" }}>
          No heal steps yet. Add your first below — put the cheapest model first.
        </p>
      </section>
    );
  }

  return (
    <section className="stack" style={{ gap: "var(--sp3)" }}>
      {err && <div className="err">{err}</div>}
      {items.map((s, i) => (
        <StepRow
          key={s.id}
          step={s}
          index={i}
          expanded={expandedId === s.id}
          dragOver={overIndex === i}
          encryptionConfigured={encryptionConfigured}
          onToggle={() => setExpandedId((cur) => (cur === s.id ? null : s.id))}
          onDragStart={() => (dragIndex.current = i)}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIndex(i);
          }}
          onDrop={() => onDrop(i)}
          reload={reload}
        />
      ))}
    </section>
  );
}

function StepRow({
  step,
  index,
  expanded,
  dragOver,
  encryptionConfigured,
  onToggle,
  onDragStart,
  onDragOver,
  onDrop,
  reload,
}: {
  step: Step;
  index: number;
  expanded: boolean;
  dragOver: boolean;
  encryptionConfigured: boolean;
  onToggle: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  reload: () => void;
}) {
  const willSkip = !step.enabled || !step.hasKey;
  const skipWhy = !step.enabled ? "disabled" : !step.hasKey ? "no key set" : "";

  return (
    <div
      className="card"
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        padding: 0,
        overflow: "hidden",
        outline: dragOver ? "2px solid var(--kintsugi)" : "none",
        opacity: step.enabled ? 1 : 0.6,
      }}
    >
      {/* Collapsed header — draggable (only when not expanded, so inputs stay selectable). */}
      <div
        className="row-gap"
        draggable={!expanded}
        onDragStart={onDragStart}
        onClick={onToggle}
        style={{ alignItems: "center", gap: "var(--sp3)", padding: "var(--sp3) var(--sp4)", cursor: "pointer" }}
      >
        <span
          title="Drag to reorder"
          style={{ cursor: "grab", color: "var(--iron)", userSelect: "none", fontSize: "1.1em" }}
        >
          ⠿
        </span>
        <span className="muted sm" style={{ width: 16, textAlign: "right" }}>
          {index + 1}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {step.name}
          </div>
          <div className="muted sm">
            {formatLabel(step.format)} · {step.model}
          </div>
        </div>
        <span className="pill">
          ${step.price.input}/${step.price.output}
        </span>
        <span className="muted sm" style={{ width: 90 }}>
          {step.hasKey ? `key ${step.hint}` : "no key"}
        </span>
        {willSkip ? (
          <span className="pill hold" title={`Will be skipped — ${skipWhy}`}>
            skipped
          </span>
        ) : (
          <span className="pill live" title="Active — this step will run">
            <span className="live-dot" /> on
          </span>
        )}
        <span className="muted" style={{ width: 14 }}>
          {expanded ? "▾" : "▸"}
        </span>
      </div>

      {expanded && (
        <StepEditor step={step} encryptionConfigured={encryptionConfigured} reload={reload} />
      )}
    </div>
  );
}

function StepEditor({
  step,
  encryptionConfigured,
  reload,
}: {
  step: Step;
  encryptionConfigured: boolean;
  reload: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => formFromStep(step));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const set = (patch: Partial<FormState>) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api(`/heal/steps/${step.id}`, { method: "PATCH", body: JSON.stringify(formToBody(form)) });
      setSaved(true);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to save step");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/heal/steps/${step.id}`, { method: "DELETE" });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to delete step");
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div
      className="stack"
      style={{ gap: "var(--sp4)", padding: "var(--sp4)", borderTop: "1px solid var(--rule)" }}
    >
      <StepFields
        form={form}
        set={set}
        encryptionConfigured={encryptionConfigured}
        keyPlaceholder={step.hasKey ? `paste a new key to rotate (current ${step.hint})` : "paste API key"}
      />

      {err && <div className="err">{err}</div>}

      <TestStepButton form={form} id={step.id} />

      <div className="row-gap" style={{ gap: "var(--sp3)", alignItems: "center" }}>
        <button className="btn danger" disabled={busy} onClick={() => setConfirming(true)}>
          Delete
        </button>
        <div style={{ flex: 1 }} />
        <span className="muted sm">updated {fmt(step.updatedAt)}</span>
        {saved && <span className="muted sm">Saved.</span>}
        <button className="btn seam" disabled={busy || !formValid(form)} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      {confirming && (
        <ConfirmModal
          title="Delete this heal step?"
          body={
            <>
              <strong>{step.name}</strong> ({formatLabel(step.format)} · {step.model}) will be removed
              from the ladder. Its key is deleted too.
            </>
          }
          confirmLabel="Delete step"
          danger
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

interface ProbeResult {
  ok: boolean;
  latencyMs: number;
  sample?: string;
  error?: string;
}

/** One tiny live call to the step's endpoint+key+model — proves it works before you rely on it. */
function TestStepButton({ form, id }: { form: FormState; id?: string }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ProbeResult | null>(null);

  // Testable when there's a model, a key source (typed key or a saved step's stored key), and — for
  // the openai format — an endpoint.
  const canTest =
    form.model.trim() !== "" &&
    (form.apiKey.trim() !== "" || id !== undefined) &&
    (form.format !== "openai" || form.endpoint.trim() !== "");

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      const body: Record<string, unknown> = {
        format: form.format,
        endpoint: form.endpoint.trim() || undefined,
        model: form.model.trim(),
      };
      if (form.apiKey.trim()) body.apiKey = form.apiKey.trim();
      else if (id) body.id = id;
      setResult(await api<ProbeResult>("/heal/test", { method: "POST", body: JSON.stringify(body) }));
    } catch (e) {
      setResult({ ok: false, latencyMs: 0, error: e instanceof Error ? e.message : "test failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row-gap" style={{ alignItems: "center", gap: "var(--sp2)" }}>
      <button className="btn ghost sm" disabled={busy || !canTest} onClick={() => void run()}>
        {busy ? "Testing…" : "Test this step"}
      </button>
      {result &&
        (result.ok ? (
          <span className="muted sm">
            ✓ {result.latencyMs}ms{result.sample ? ` · “${result.sample}”` : ""}
          </span>
        ) : (
          <span className="err sm">✗ {result.error}</span>
        ))}
    </div>
  );
}

function AddStep({ encryptionConfigured, reload }: { encryptionConfigured: boolean; reload: () => void }) {
  // The LADDER is the focus; adding a step is secondary — so the creation form stays collapsed behind
  // a quiet affordance and only unfolds when you actually want to add one.
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const collapse = () => {
    setOpen(false);
    setForm(emptyForm);
    setErr(null);
  };

  async function add() {
    setBusy(true);
    setErr(null);
    try {
      await api("/heal/steps", { method: "POST", body: JSON.stringify(formToBody(form)) });
      collapse();
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to add step");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div>
        <button className="btn ghost" onClick={() => setOpen(true)}>
          + Add heal step
        </button>
      </div>
    );
  }

  return (
    <section className="card">
      <div className="section-head">
        <h2>Add a heal step</h2>
        <span className="muted sm">appended at the end — drag it up after</span>
      </div>

      <div style={{ marginTop: "var(--sp4)" }}>
        <StepFields form={form} set={set} encryptionConfigured={encryptionConfigured} keyPlaceholder="paste API key" />
      </div>

      {err && <div className="err mt">{err}</div>}

      <div className="row-gap" style={{ marginTop: "var(--sp5)", gap: "var(--sp3)", alignItems: "center" }}>
        <TestStepButton form={form} />
        <div style={{ flex: 1 }} />
        <button className="btn ghost" disabled={busy} onClick={collapse}>
          Cancel
        </button>
        <button className="btn" disabled={busy || !formValid(form)} onClick={() => void add()}>
          {busy ? "Adding…" : "+ Add step"}
        </button>
      </div>
    </section>
  );
}
